import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { AttestResponse, AutoRefreshResponse, EmployerHeadcountDeclaration, EmployerRatioResponse, RatioAlert } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function EmployerRatio() {
  const { employerEntityId = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER);
  const isChief = hasAnyRole(persona?.roles, M16_ROLES.CHIEF_INSPECTOR);
  const isSelfEmployer = persona?.kind === 'employer' && persona.actingForBusinessId === employerEntityId;
  const actingFor = isSelfEmployer ? persona.actingForBusinessId : undefined;

  const rq = useApiQuery<EmployerRatioResponse>(`/api/v1/m16/employers/${employerEntityId}/ratio`, actingFor);

  if (rq.loading) return <Spinner label="Loading ratio…" />;
  if (rq.error) return <ErrorNote message={rq.error} />;
  if (!rq.data) return <ErrorNote message="Not found" />;
  const { monitor, declarations } = rq.data;

  return (
    <div>
      <PageHeader title={employerEntityId} subtitle="Expat/local headcount ratio monitoring" actions={<StatusBadge status={monitor.status} />} />

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <StatCard label="Expat headcount" value={monitor.expatCount} />
        <StatCard label="Local headcount" value={monitor.localCount} />
        <StatCard
          label="Current ratio"
          value={`${monitor.ratioPercent}%`}
          tone={monitor.status === 'BREACH' ? 'bad' : monitor.status === 'OK' ? 'good' : 'default'}
          hint={`Threshold ${monitor.thresholdPercent}%`}
        />
        <StatCard label="Last evaluated" value={monitor.lastEvaluatedAt ? formatDateTime(monitor.lastEvaluatedAt) : '—'} hint={monitor.source} />
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <AlertsCard alerts={monitor.alerts ?? []} canAcknowledge={isChief} onDone={rq.reload} />
          <DeclarationsCard declarations={declarations} canAttest={isOfficer || isChief} onDone={rq.reload} />
        </div>

        <div className="space-y-4">
          {isOfficer || isSelfEmployer ? (
            <DeclareCard employerEntityId={employerEntityId} actingFor={actingFor} onDeclared={rq.reload} />
          ) : null}
          {isChief ? <ThresholdCard employerEntityId={employerEntityId} current={monitor.thresholdPercent} onSet={rq.reload} /> : null}
          {isOfficer || isChief ? <AutoRefreshCard employerEntityId={employerEntityId} onRefreshed={rq.reload} /> : null}
        </div>
      </div>
    </div>
  );
}

function AlertsCard({ alerts, canAcknowledge, onDone }: { alerts: RatioAlert[]; canAcknowledge: boolean; onDone: () => void }) {
  if (alerts.length === 0) return null;
  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Threshold-breach alerts</h2>
      <ul className="space-y-2">
        {alerts.map((a) => (
          <AlertRow key={a.id} alert={a} canAcknowledge={canAcknowledge} onDone={onDone} />
        ))}
      </ul>
    </div>
  );
}

function AlertRow({ alert, canAcknowledge, onDone }: { alert: RatioAlert; canAcknowledge: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const acknowledge = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/ratio-alerts/${alert.id}/acknowledge`, { note: note.trim() || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not acknowledge');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg border border-slate-100 bg-slate-50 px-3.5 py-3 text-sm">
      <div className="flex items-center justify-between">
        <span>
          {alert.ratioPercent}% vs {alert.thresholdPercent}% threshold — {formatDateTime(alert.triggeredAt)}
        </span>
        {alert.acknowledgedAt ? <StatusBadge status="OK" /> : <StatusBadge status="BREACH" />}
      </div>
      {alert.acknowledgedAt ? (
        <div className="mt-1 text-xs text-slate-500">
          Acknowledged {formatDateTime(alert.acknowledgedAt)}
          {alert.note ? ` — ${alert.note}` : ''}
        </div>
      ) : canAcknowledge ? (
        open ? (
          <div className="mt-2 space-y-1.5">
            {error ? <ErrorNote message={error} /> : null}
            <input className="field-input !py-1 text-xs" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-1.5">
              <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={acknowledge} disabled={busy}>
                {busy ? 'Saving…' : 'Confirm acknowledge'}
              </button>
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-secondary mt-1.5 !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
            Acknowledge
          </button>
        )
      ) : null}
    </li>
  );
}

function DeclarationsCard({
  declarations,
  canAttest,
  onDone,
}: {
  declarations: EmployerHeadcountDeclaration[];
  canAttest: boolean;
  onDone: () => void;
}) {
  return (
    <div className="card overflow-x-auto p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Headcount declarations</h2>
      {declarations.length === 0 ? (
        <p className="text-xs text-slate-400">No declarations yet.</p>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              <th className="table-head">Period</th>
              <th className="table-head">Expat</th>
              <th className="table-head">Local</th>
              <th className="table-head">Source</th>
              <th className="table-head">Declared</th>
              <th className="table-head">Attested</th>
              <th className="table-head" />
            </tr>
          </thead>
          <tbody>
            {declarations.map((d) => (
              <DeclarationRow key={d.id} declaration={d} canAttest={canAttest} onDone={onDone} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DeclarationRow({ declaration, canAttest, onDone }: { declaration: EmployerHeadcountDeclaration; canAttest: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attest = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<AttestResponse>(`/api/v1/m16/headcount-declarations/${declaration.id}/attest`);
      if (res.alertTriggered) {
        // no-op — reload below picks up the new alert row too
      }
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Attest failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell text-xs font-semibold">{declaration.periodLabel}</td>
      <td className="table-cell text-xs tabular-nums">{declaration.expatCount}</td>
      <td className="table-cell text-xs tabular-nums">{declaration.localCount}</td>
      <td className="table-cell text-xs">{declaration.source}</td>
      <td className="table-cell text-xs">{formatDateTime(declaration.declaredAt)}</td>
      <td className="table-cell text-xs">{declaration.attestedAt ? formatDateTime(declaration.attestedAt) : '—'}</td>
      <td className="table-cell text-xs">
        {error ? <ErrorNote message={error} /> : null}
        {!declaration.attestedAt && canAttest ? (
          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={attest} disabled={busy}>
            {busy ? 'Attesting…' : 'Attest'}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function DeclareCard({ employerEntityId, actingFor, onDeclared }: { employerEntityId: string; actingFor?: string; onDeclared: () => void }) {
  const [periodLabel, setPeriodLabel] = useState(() => new Date().toISOString().slice(0, 7));
  const [expatCount, setExpatCount] = useState(0);
  const [localCount, setLocalCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/employers/${employerEntityId}/headcount-declarations`, { periodLabel, expatCount, localCount }, { actingFor });
      onDeclared();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Declaration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Declare headcounts</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Period" hint="e.g. 2026-07" className="mb-2">
        <input className="field-input" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} />
      </Field>
      <div className="mb-2 grid grid-cols-2 gap-2">
        <Field label="Expat count" className="!mb-0">
          <input type="number" min={0} className="field-input" value={expatCount} onChange={(e) => setExpatCount(Number(e.target.value))} />
        </Field>
        <Field label="Local count" className="!mb-0">
          <input type="number" min={0} className="field-input" value={localCount} onChange={(e) => setLocalCount(Number(e.target.value))} />
        </Field>
      </div>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || !periodLabel.trim()}>
        {busy ? 'Declaring…' : 'Declare'}
      </button>
    </div>
  );
}

function ThresholdCard({ employerEntityId, current, onSet }: { employerEntityId: string; current: number; onSet: () => void }) {
  const [thresholdPercent, setThresholdPercent] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m16/employers/${employerEntityId}/ratio/threshold`, { thresholdPercent });
      onSet();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not set threshold');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Set threshold override</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Threshold %" className="mb-2">
        <input type="number" min={0.01} max={100} step={0.1} className="field-input" value={thresholdPercent} onChange={(e) => setThresholdPercent(Number(e.target.value))} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || thresholdPercent <= 0}>
        {busy ? 'Saving…' : 'Set threshold'}
      </button>
    </div>
  );
}

function AutoRefreshCard({ employerEntityId, onRefreshed }: { employerEntityId: string; onRefreshed: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AutoRefreshResponse | null>(null);

  const refresh = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post<AutoRefreshResponse>(`/api/v1/m16/employers/${employerEntityId}/ratio/auto-refresh`);
      setResult(res);
      onRefreshed();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Auto-refresh unavailable');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Automatic refresh (M03+M08)</h2>
      <InfoNote message="Honest 'try the automatic path' seam — expected to return 501 DEPENDENCY_NOT_LIVE until M03's aggregate-by-employer contract and M08 PAYE data both exist. Declared headcounts above are the real v1 path." />
      {error ? (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {result ? <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">Refreshed from {result.monitor.source}.</div> : null}
      <button className="btn-secondary mt-3 w-full" onClick={refresh} disabled={busy}>
        {busy ? 'Trying…' : 'Try automatic refresh'}
      </button>
    </div>
  );
}
